import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Store } from '@ngrx/store';
import * as echarts from 'echarts';
import { EChartsOption } from 'echarts';
import { cloneDeep } from 'lodash-es';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { ChartTypes } from '../../../../models/IChartTypes';
import { IReportGraph } from '../../../../models/IReportGraph';
import { ReportPageData } from '../../../../models/IReportPageData';
import { OptionsService } from '../../../../services/options.service';
import { ReportDetailService } from '../../../../services/report-details.service';
import { ReportService } from '../../../../services/report.service';
import { ChartConfigService } from '../../../../services/chart-config.service';
import { ThemeService } from '../../../../services/theme.service';
import { selectMarketShowAsPercent } from '../../../../store/selectors/market-position.selector';
import { IAppState } from '../../../../store/state/app.state';

@Component({
  selector: 'app-market-quest-chart',
  templateUrl: './market-quest-chart.component.html',
  styleUrls: ['./market-quest-chart.component.scss']
})
export class MarketQuestChartComponent implements OnInit, OnDestroy, OnChanges {
  @Input() hideFirmNames = false;
  @Input() hideTitle = false;
  @Input() downloadableImageType: ChartTypes;
  @Output() chartReady = new EventEmitter<boolean>();
  @Input() chartSourceData: ReportPageData;
  @Input() overridePrimaryColor: string;
  @Input() previewChart = false;
  @Input() disableEmphasis = false;
  public title: string;
  public primaryYAxis: any;
  public primaryXAxis: any;
  public animation: any;
  public chartData: IReportGraph[];
  public titleStyle: any;
  public subTitleStyle: any;
  public chartArea: any;
  public marker: any;
  public margin: any;
  public selectChartLoading$: Observable<boolean>;
  public showChart = true;
  public transposed: boolean;
  public randomIdStr: string;
  public sourceType: string;
  public subTitle: string;
  public cornerRadius: any;
  public data: ReportPageData;
  public echartsOptions: EChartsOption = {};
  private chartInstance: echarts.ECharts;
  private chartSizeType: ChartTypes;
  private showAsPercentage = false;
  private isConsolidatedMLS = false;
  subs = new Subscription();

  constructor(
    private store: Store<IAppState>,
    private reportService: ReportService,
    private optionsService: OptionsService,
    private ChartConfigService: ChartConfigService,
    private breakpointObserver: BreakpointObserver,
    private cssService: ThemeService,
    private reportDetailService: ReportDetailService
  ) {
    this.randomIdStr = MarketQuestChartComponent.randomId();
    this.subs.add(
      this.store
        .select(selectMarketShowAsPercent)
        .pipe(filter(resp => resp !== undefined))
        .subscribe(bool => {
          if (this.showAsPercentage !== bool) {
            this.reloadChartFromNewData();
          }
          this.showAsPercentage = bool;
        })
    );
  }

  private static detectSize(sizeMatch: BreakpointState) {
    if (sizeMatch.breakpoints['(min-width: 600px)']) {
      return sizeMatch.breakpoints['(min-width: 960px)'] ? ChartTypes.desktop : ChartTypes.tablet;
    } else {
      return ChartTypes.mobile;
    }
  }

  private static fetchChartConfig(size: ChartTypes, target: MarketQuestChartComponent) {
    return Object.assign(target, target.ChartConfigService.getConfig(size));
  }

  private static getMaxY(graphData: IReportGraph[], showPercent: boolean) {
    if (showPercent) {
      return graphData.reduce(
        (max, p) => (p.marketSharePercentage > max ? p.marketSharePercentage : max),
        graphData[0].marketSharePercentage
      );
    } else {
      return graphData.reduce((max, p) => (p.reportMeasureValue > max ? p.reportMeasureValue : max), graphData[0].reportMeasureValue);
    }
  }

  private static randomId() {
    return Math.random()
      .toString(36)
      .replace(/[^a-z]+/g, '')
      .substring(2, 10);
  }

  private static getMaxScaleByDeviceSize(maxVal: number, smallChat: boolean, medChart: boolean) {
    return maxVal * (smallChat ? 1.5 : medChart ? 1.4 : 1.1);
  }

  private static convertReportGraphNamesToSmall(chartData: IReportGraph[]) {
    return chartData.map((val, idx) => {
      val.levelEntityName = `${idx + 1}`;
      return val;
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes.chartSourceData?.firstChange || !changes.downloadableImageType?.firstChange) {
      this.showChart = false;
      this.chartInit();
      setTimeout(() => {
        this.initializeAndShowChart();
        this.showChart = true;
      }, 1);
    }
  }

  ngOnInit() {
    this.chartInit();
    this.subs.add(
      this.breakpointObserver
        .observe(['(min-width: 600px)', '(min-width: 960px)'])
        .pipe(distinctUntilChanged())
        .subscribe(sizeMatches => {
          if (this.chartSizeType !== MarketQuestChartComponent.detectSize(sizeMatches)) {
            this.reloadChartFromNewData();
          }
          this.chartSizeType = this.downloadableImageType || MarketQuestChartComponent.detectSize(sizeMatches);
          this.transposed = [ChartTypes.mobile, ChartTypes.tall].includes(this.chartSizeType);
          if (this.data?.reportDetail) {
            MarketQuestChartComponent.fetchChartConfig(this.chartSizeType, this);
          }
        })
    );
  }

  getBarColor(index: number): string {
    const barColor = this.previewChart ? '#B7B9B9' : '#dcdcdd';
    const barCount = this.chartData?.length || 5;
    const seriesColor: string[] = Array(barCount).fill(barColor);
    let cbBlue = this.cssService?.colorPrimary || '#1f69ff';
    if (!!this.overridePrimaryColor) {
      cbBlue = this.overridePrimaryColor;
    }
    const cbIndex = this.getCbBarIndex(barCount);
    if (cbIndex >= 0) {
      seriesColor.splice(cbIndex, 1, cbBlue);
    }
    return seriesColor[index];
  }

  private getCbBarIndex(barCount: number): number {
    const nameIndex = this.getCbBarIndexByName();
    if (nameIndex >= 0) {
      return nameIndex;
    }
    if (this.data.cbposition >= 1 && this.data.cbposition <= barCount) {
      return this.setCBPosition(this.data.cbposition) - 1;
    }
    return -1;
  }

  // Exact brand match so "Coldwell Banker Southern Realty" and similar affiliates are not highlighted
  private getCbBarIndexByName(): number {
    if (!this.chartData?.length) {
      return -1;
    }
    if (this.cssService?.isSIR()) {
      return this.chartData.findIndex(d => (d.levelEntityName || '').toLowerCase().includes('sotheby'));
    }
    const cbNames = ['coldwell banker', 'coldwell banker realty'];
    return this.chartData.findIndex(d => cbNames.includes((d.levelEntityName || '').trim().toLowerCase()));
  }

  getAxisLabel(text: string): string {
    return this.reportService.trimLabels(text, this.chartSizeType).replace(/<br\s*\/?>/gi, '\n');
  }

  getDataLabel(index: number, rawText: string): string {
    const cbPosition = this.setCBPosition(this.data.cbposition);
    return this.convertUnitsPercentDataPoint(this.convertToConsolidated(rawText, index, cbPosition));
  }

  getDataLabelColor(index: number): string {
    if (index !== this.getCbBarIndex(this.chartData?.length || 5) && this.previewChart) {
      return '#8B8C8F';
    }
    return undefined;
  }

  chartLoaded() {
    this.chartReady.next(true);
    this.chartReady.complete();
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
    if (this.chartInstance) {
      this.chartInstance.dispose();
    }
  }

  private convertUnitsPercentDataPoint(val: string) {
    const isUnits = this.data.reportDetail.reportHeaderEntity.msTypeName.toLowerCase().includes('units');
    const showPercentage = this.showAsPercentage;
    if (!val) {
      return '';
    }
    if (showPercentage) {
      return val + '%';
    }
    if (!isUnits) {
      return this.optionsService.numFormatter(Number(val.replace(/,/g, '')), 2);
    }
    return val;
  }

  private convertToConsolidated(text: string, index: number, cbPosition: number) {
    if (this.isConsolidatedMLS) {
      return cbPosition === index + 1 ? text : '';
    }
    return text;
  }

  private setCBPosition(cbPosition: number) {
    if (!this.transposed) {
      return cbPosition;
    }
    const barCount = this.chartData?.length || 5;
    return barCount - cbPosition + 1;
  }

  private initializeAndShowChart() {
    const lg = [ChartTypes.previewLarge, ChartTypes.mobile, ChartTypes.tall].includes(this.chartSizeType);
    const sm = [ChartTypes.previewSmall].includes(this.chartSizeType);
    const isReportPage = [ChartTypes.reportPage, ChartTypes.reportPageSm].includes(this.downloadableImageType);

    if (!this.hideTitle) {
      this.setChartHeaderTitles(isReportPage);
    }

    this.primaryYAxis.maximum = MarketQuestChartComponent.getMaxScaleByDeviceSize(
      MarketQuestChartComponent.getMaxY(this.data.reportDetail.reportGraphEntities, this.showAsPercentage),
      sm,
      lg
    );
    this.sourceType = this.showAsPercentage ? 'marketSharePercentage' : 'reportMeasureValue';
    this.chartData = this.reportService.adjustChartLayoutByData(this.data.reportDetail.reportGraphEntities, this.chartSizeType);
    if (this.downloadableImageType === ChartTypes.reportPageSm) {
      this.chartData = MarketQuestChartComponent.convertReportGraphNamesToSmall(this.chartData);
    }
    this.buildEchartsOptions();
  }

  onChartInit(ec: echarts.ECharts): void {
    this.chartInstance = ec;
    if (this.downloadableImageType) {
      ec.on('finished', () => this.chartLoaded());
    }
  }

  private buildEchartsOptions() {
    if (!this.chartData) {
      return;
    }
    const isHorizontal = this.transposed;
    const categories = this.chartData.map(d => d.levelEntityName);
    const seriesData = this.chartData.map((d, index) => ({
      value: Number(d[this.sourceType]),
      itemStyle: {
        color: this.getBarColor(index),
        borderRadius: this.getBarBorderRadius(isHorizontal)
      },
      label: {
        color: this.getDataLabelColor(index) || this.marker?.dataLabel?.font?.color || '#ffffff'
      }
    }));

    const valueAxis: any = {
      type: 'value',
      min: 0,
      max: this.primaryYAxis?.maximum,
      // Fixed 6 equal gaps; ECharts' auto "nice" interval would not guarantee this
      interval: this.primaryYAxis?.maximum ? this.primaryYAxis.maximum / 8 : undefined,
      // show must stay true, otherwise ECharts hides the splitLine along with the axis
      show: true,
      axisLabel: { show: false },
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: {
        show: this.downloadableImageType !== ChartTypes.exportPreview,
        lineStyle: { color: '#E9E9E9', width: 1 }
      }
    };

    const categoryAxis: any = {
      type: 'category',
      data: categories,
      inverse: isHorizontal,
      axisTick: { show: false },
      axisLine: {
        show: true,
        lineStyle: { color: this.primaryXAxis?.lineStyle?.color || '#ffffff' }
      },
      axisLabel: {
        color: this.primaryXAxis?.labelStyle?.color || '#ffffff',
        fontFamily: this.primaryXAxis?.labelStyle?.fontFamily,
        fontSize: this.parseSize(this.primaryXAxis?.labelStyle?.size),
        interval: 0,
        formatter: (value: string) => this.getAxisLabel(value)
      }
    };

    this.echartsOptions = {
      backgroundColor: 'transparent',
      animation: !!this.animation?.enable,
      title: this.hideTitle
        ? undefined
        : {
            text: this.title,
            subtext: this.subTitle,
            left: this.downloadableImageType === ChartTypes.exportPreview ? 'center' : 'left',
            textStyle: {
              fontFamily: this.titleStyle?.fontFamily,
              fontWeight: this.titleStyle?.fontWeight,
              fontSize: this.parseSize(this.titleStyle?.size),
              color: this.titleStyle?.color
            },
            subtextStyle: {
              fontFamily: this.subTitleStyle?.fontFamily,
              fontWeight: this.subTitleStyle?.fontWeight,
              fontSize: this.parseSize(this.subTitleStyle?.size),
              color: this.subTitleStyle?.color
            }
          },
      grid: {
        containLabel: true,
        left: this.margin?.left ?? 16,
        right: this.margin?.right ?? 16,
        top: (this.margin?.top ?? (this.hideTitle ? 24 : 64)) + 12,
        bottom: this.margin?.bottom ?? 16
      },
      xAxis: isHorizontal ? valueAxis : categoryAxis,
      yAxis: isHorizontal ? categoryAxis : valueAxis,
      series: [
        {
          type: 'bar',
          data: seriesData,
          barWidth: '55%',
          silent: true,
          cursor: 'default',
          emphasis: { disabled: true },
          label: {
            show: true,
            position: isHorizontal ? 'right' : 'top',
            fontFamily: this.marker?.dataLabel?.font?.fontFamily,
            fontSize: this.parseSize(this.marker?.dataLabel?.font?.size),
            fontWeight: this.downloadableImageType === ChartTypes.exportPreview ? 'bold' : undefined,
            formatter: (params: any) => this.getDataLabel(params.dataIndex, this.formatRawValue(params.value))
          }
        }
      ]
    };
  }

  private getBarBorderRadius(isHorizontal: boolean): number[] {
    const tl = this.cornerRadius?.topLeft || 0;
    const tr = this.cornerRadius?.topRight || 0;
    return isHorizontal ? [0, tr, tl, 0] : [tl, tr, 0, 0];
  }

  private formatRawValue(value: number): string {
    return value === null || value === undefined ? '' : value.toLocaleString('en-US');
  }

  private parseSize(size: string): number | undefined {
    if (!size) {
      return undefined;
    }
    const parsed = parseInt(size, 10);
    return isNaN(parsed) ? undefined : parsed;
  }

  private setChartHeaderTitles(useSubtitle: boolean) {
    const titleWithoutSubtitle =
      `Leading ${this.data.reportDetail.reportHeaderEntity.groupByLevel} by ` + this.data.reportDetail.reportHeaderEntity.msTypeName + ' ';
    const titleWithSubtitle = titleWithoutSubtitle + '(' + this.data.reportDetail.reportHeaderEntity.displayDateRange + ')';
    const title = useSubtitle ? titleWithoutSubtitle : titleWithSubtitle;
    const subTitle = useSubtitle ? this.data.reportDetail.reportHeaderEntity.displayDateRange : '';

    this.title = this.downloadableImageType === ChartTypes.exportPreview ? title.toUpperCase() : title;
    this.subTitle = this.downloadableImageType === ChartTypes.exportPreview ? subTitle.toUpperCase() : subTitle;
  }

  private reloadChartFromNewData() {
    this.showChart = false;
    setTimeout(() => {
      this.initializeAndShowChart();
      this.showChart = true;
    }, 1);
  }

  private chartInit() {
    this.data = cloneDeep(this.chartSourceData);
    this.chartSizeType = this.downloadableImageType || null;
    this.isConsolidatedMLS = this.data.reportDetail.reportHeaderEntity.mlsId === 'COSC';
    this.data.reportDetail.reportGraphEntities = this.reportDetailService.remapGraphEntryNames(
      this.data.reportDetail,
      this.data.cbposition
    );
    MarketQuestChartComponent.fetchChartConfig(this.chartSizeType, this);
    this.initializeAndShowChart();
    this.animation = {
      enable: this.downloadableImageType === undefined
    };
  }
}
